import { useRef, useState, useCallback } from 'react';

const AW = 62; // largeur d'un bouton d'action (px)

/**
 * Enveloppe une ligne de liste et révèle des actions au glissement horizontal :
 * - glisser vers la DROITE révèle `leftAction` (une seule action, ex: marquer lu)
 * - glisser vers la GAUCHE révèle `rightActions` (jusqu'à 2, ex: éditer / supprimer)
 * Un tap simple (sans glissement) déclenche `onOpen()`, sauf si un tiroir est
 * déjà ouvert : dans ce cas il se referme d'abord.
 */
export default function SwipeRow({ onOpen, leftAction, rightActions = [], children }) {
  const [x, setX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startRef = useRef(null);
  const wrapRef = useRef(null);

  const leftW  = leftAction ? AW : 0;
  const rightW = rightActions.length * AW;

  const close = useCallback(() => setX(0), []);

  const onPointerDown = e => {
    if (e.target.closest('button')) return; // laisser les boutons déjà révélés gérer leur propre clic
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    startRef.current = { px: e.clientX, baseX: x, moved: false };
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = e => {
    const s = startRef.current;
    if (!s) return;
    const delta = e.clientX - s.px;
    if (Math.abs(delta) > 4) s.moved = true;
    setX(Math.max(-rightW, Math.min(leftW, s.baseX + delta)));
  };
  const endDrag = () => {
    const s = startRef.current;
    setDragging(false);
    if (!s) return;
    if (!s.moved) {
      // Tap simple : referme un tiroir déjà ouvert, sinon ouvre la fiche
      if (s.baseX !== 0) setX(0);
      else {
        const r = wrapRef.current?.getBoundingClientRect();
        onOpen?.(r ? { top:r.top, left:r.left, width:r.width, height:r.height } : null);
      }
    } else {
      setX(cur => {
        if (cur > leftW * 0.5) return leftW;
        if (cur < -rightW * 0.5) return -rightW;
        return 0;
      });
    }
    startRef.current = null;
  };

  return (
    <div ref={wrapRef} style={{ position:'relative', overflow:'hidden', borderRadius:'12px' }}>
      {leftAction && (
        <div style={{ position:'absolute', top:0, bottom:0, left:0, width:leftW, display:'flex' }}>
          <button onClick={()=>{leftAction.onClick();close();}}
            style={{width:'100%',border:'none',background:leftAction.color,color:'#fff',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'2px',fontSize:'10px',fontWeight:700,cursor:'pointer',fontFamily:"'Inter',sans-serif"}}>
            <span style={{fontSize:'17px'}}>{leftAction.icon}</span>{leftAction.label}
          </button>
        </div>
      )}
      {rightActions.length > 0 && (
        <div style={{ position:'absolute', top:0, bottom:0, right:0, width:rightW, display:'flex' }}>
          {rightActions.map((a,i) => (
            <button key={i} onClick={()=>{a.onClick();close();}}
              style={{width:AW,border:'none',background:a.color,color:'#fff',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'2px',fontSize:'10px',fontWeight:700,cursor:'pointer',fontFamily:"'Inter',sans-serif"}}>
              <span style={{fontSize:'17px'}}>{a.icon}</span>{a.label}
            </button>
          ))}
        </div>
      )}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        style={{
          transform:`translateX(${x}px)`,
          transition: dragging ? 'none' : 'transform .25s cubic-bezier(.2,.8,.2,1)',
          touchAction:'pan-y',
          position:'relative', zIndex:1,
        }}>
        {children}
      </div>
    </div>
  );
}
